/**
 * CV Upload Step Component for Onboarding Process
 *
 * This component handles CV file upload and AI-powered analysis for the onboarding process.
 * It provides drag & drop functionality, file validation, real-time progress tracking,
 * and integrates with the backend WebSocket API for CV analysis.
 *
 * Features:
 * - Drag & drop file upload with visual feedback
 * - File type validation (PDF, DOC, DOCX)
 * - File size validation (max 10MB)
 * - Real-time upload progress tracking
 * - AI-powered CV analysis via WebSocket
 * - Responsive design with mobile support
 * - Error handling and retry functionality
 * - Integration with existing API infrastructure
 *
 * @author Development Team
 * @version 1.0.0
 */

import { CheckCircle, CloudUpload, Delete, Description, Refresh } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import { ICvAnalyse } from '@sharedInterfaces/websocket/ICvAnalyse';
import getUploadedFileContent from '@src/APIs/graphQl/Company/getUploadedFileContent';
import getUploadURL from '@src/APIs/graphQl/Company/getUploadURL';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { uploadFile } from '@src/App/Components/Dialogs/AnalyseSkillsDialog/AnalyseCvDialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import type { AppState } from '@store/store';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { mockAnalysisResults } from '../data/mockAnalysisData';
import type { CertificateItem, OnboardingData, RoleItem, SkillItem } from '../types/OnboardingTypes';

/**
 * Props interface for CVUploadStep component
 */
interface CVUploadStepProps {
    /** Callback to proceed to next step */
    onNext: () => void;
    /** Callback to go back to previous step */
    onBack: () => void;
    /** Callback when file is uploaded */
    onFileUpload: (file: File) => void;
    /** Callback to update agent message */
    setAgentMessage: (message: string) => void;
    /** Current onboarding data */
    data: OnboardingData;
    /** Callback to update onboarding data */
    onDataUpdate: (updates: Partial<OnboardingData>) => void;
}

/**
 * Analysis results interface for internal state management
 */
interface AnalysisResults {
    skills: SkillItem[];
    certificates: CertificateItem[];
    roles: RoleItem[];
    description: string;
}

/**
 * File upload and CV analysis component
 *
 * Handles the complete flow from file upload to AI analysis results.
 * Provides responsive UI with drag & drop functionality and real-time feedback.
 */
export default function CVUploadStep({ onNext, onFileUpload, setAgentMessage, data, onDataUpdate }: CVUploadStepProps) {
    // Theme and responsive hooks
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const showErrorDialog = useErrorDialog();

    // Redux state
    const companyId = useSelector((state: AppState) => state.company.companyId);

    // Component state
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);

    /**
     * Initialize component with welcome message
     */
    useEffect(() => {
        setAgentMessage(
            'Perfekt! Jetzt laden wir Ihren Lebenslauf hoch. Ich analysiere ihn automatisch und erkenne Ihre Fähigkeiten, Erfahrungen und Qualifikationen. Ziehen Sie einfach Ihre Datei hierher oder klicken Sie auf "Datei auswählen".',
        );
    }, [setAgentMessage]);

    /**
     * Handle drag events for drag & drop functionality
     */
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    /**
     * Handle file drop event
     */
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    };

    /**
     * Handle file selection via input
     */
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files[0]);
        }
    };

    /**
     * Main file upload and analysis handler
     *
     * Validates file, uploads to S3, extracts content, and triggers AI analysis
     */
    /**
     * Load mock data for testing without CV upload
     */
    const handleLoadMockData = () => {
        setError(null);
        setUploading(true);
        setUploadProgress(0);

        // Simulate upload progress
        const progressInterval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(progressInterval);

                    // Set mock file
                    const mockFile = new File(['mock content'], 'mock-cv.pdf', { type: 'application/pdf' });
                    setUploadedFile(mockFile);

                    // Set mock analysis results
                    const results: AnalysisResults = {
                        skills: mockAnalysisResults.skills,
                        certificates: mockAnalysisResults.certificates,
                        roles: mockAnalysisResults.roles,
                        description: mockAnalysisResults.description,
                    };

                    setAnalysisResults(results);
                    setUploading(false);
                    setIsAnalyzing(false);

                    // Update parent component with mock results
                    onDataUpdate({
                        analysisResults: {
                            skills: results.skills,
                            certificates: results.certificates,
                            roles: results.roles,
                        },
                        personalInfo: {
                            ...data.personalInfo,
                            shortDescription: results.description,
                        },
                    });

                    setAgentMessage(
                        `Mock-Daten geladen! Ich habe ${results.skills.length} Fähigkeiten, ${results.certificates.length} Zertifikate und ${results.roles.length} Rollen gefunden. Du kannst nun fortfahren.`,
                    );

                    return 100;
                }
                return prev + 20;
            });
        }, 200);
    };

    const handleFileUpload = async (file: File) => {
        setError(null);

        // File type validation
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];
        if (!allowedTypes.includes(file.type)) {
            setError('Bitte wählen Sie eine PDF-, DOC- oder DOCX-Datei aus.');
            return;
        }

        // File size validation (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError('Die Datei ist zu groß. Maximale Größe: 10MB.');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // COMMENTED OUT FOR MOCK: Upload file to S3 and analyze content
            // setUploadProgress(20);
            // await getUploadURL(async (post) => {
            //   ... real upload logic ...
            // });

            // Mock implementation - load mock data instead
            handleLoadMockData();
        } catch (err) {
            console.error('Upload error:', err);
            showErrorDialog(err as Error);
            setUploading(false);
            setIsAnalyzing(false);
        }
    };

    /**
     * Remove uploaded file and reset state
     */
    const handleRemoveFile = () => {
        setUploadedFile(null);
        setUploadProgress(0);
        setError(null);
        setAnalysisResults(null);
        setIsAnalyzing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Retry upload after error
     */
    const handleRetry = () => {
        setError(null);
        setUploading(false);
        setUploadProgress(0);
        setIsAnalyzing(false);
        setAnalysisResults(null);
    };

    return (
        <Box sx={{ maxWidth: isMobile ? '100%' : 800, mx: 'auto', px: isMobile ? 1 : 0 }}>
            {/* Header */}
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 600, mb: 1 }}>
                CV Upload
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Laden Sie Ihren Lebenslauf hoch, damit unsere KI Ihre Fähigkeiten und Erfahrungen analysieren kann.
            </Typography>

            {/* Upload Area */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `2px dashed ${dragActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                    background: dragActive
                        ? alpha(theme.palette.primary.main, 0.05)
                        : alpha(theme.palette.background.paper, 0.7),
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.02),
                    },
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <CardContent sx={{ p: isMobile ? 3 : 6, textAlign: 'center' }}>
                    {/* Initial Upload State */}
                    {!uploadedFile && !uploading && (
                        <>
                            <CloudUpload
                                sx={{
                                    fontSize: isMobile ? 48 : 64,
                                    color: theme.palette.primary.main,
                                    mb: 2,
                                }}
                            />
                            <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600, mb: 1 }}>
                                Datei hier ablegen oder klicken zum Auswählen
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Unterstützte Formate: PDF, DOC, DOCX (max. 10MB)
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUpload />}
                                    size="medium"
                                    sx={{
                                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                    }}
                                >
                                    Datei auswählen
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadMockData();
                                    }}
                                >
                                    Lade MockUp Daten
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* Upload/Analysis Progress State */}
                    {(uploading || isAnalyzing) && (
                        <Box>
                            <CloudUpload
                                sx={{
                                    fontSize: isMobile ? 48 : 64,
                                    color: theme.palette.primary.main,
                                    mb: 2,
                                    animation: 'pulse 2s ease-in-out infinite',
                                }}
                            />
                            <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600, mb: 2 }}>
                                {isAnalyzing ? 'KI analysiert Ihren CV...' : 'Datei wird hochgeladen...'}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    mb: 1,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    },
                                }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {Math.round(uploadProgress)}%
                            </Typography>
                        </Box>
                    )}

                    {/* Success State */}
                    {uploadedFile && !uploading && !isAnalyzing && (
                        <Box>
                            <CheckCircle
                                sx={{
                                    fontSize: isMobile ? 48 : 64,
                                    color: theme.palette.success.main,
                                    mb: 2,
                                }}
                            />
                            <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600, mb: 1 }}>
                                {analysisResults ? 'Analyse abgeschlossen' : 'Datei erfolgreich hochgeladen'}
                            </Typography>

                            {/* File Info */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    mb: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Description color="primary" />
                                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                    {uploadedFile.name}
                                </Typography>
                                <Chip
                                    label={`${(uploadedFile.size / 1024 / 1024).toFixed(1)} MB`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>

                            {/* Analysis Results Summary */}
                            {analysisResults && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'center',
                                        mb: 2,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Chip
                                        label={`${analysisResults.skills.length} Skills`}
                                        size="small"
                                        color="primary"
                                    />

                                    <Chip
                                        label={`${analysisResults.certificates.length} Zertifikate`}
                                        size="small"
                                        color="secondary"
                                    />
                                    <Chip label={`${analysisResults.roles.length} Rollen`} size="small" color="info" />
                                </Box>
                            )}

                            {/* Remove File Button */}
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFile();
                                    }}
                                    color="error"
                                    size="small"
                                    aria-label="Datei entfernen"
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 4 }}
                    action={
                        <IconButton color="inherit" size="small" onClick={handleRetry} aria-label="Erneut versuchen">
                            <Refresh />
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Next Button - shown when analysis is complete */}
            {analysisResults && uploadedFile && !uploading && !isAnalyzing && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={() => {
                            setAgentMessage(
                                'Perfekt! Lassen Sie uns nun Ihre analysierten Daten überprüfen und anpassen.',
                            );
                            onNext();
                        }}
                        sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                            px: 4,
                            py: 1.5,
                        }}
                    >
                        Weiter
                    </Button>
                </Box>
            )}

            {/* Manual Onboarding Button - shown when no file uploaded */}
            {!uploadedFile && !uploading && !isAnalyzing && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => {
                            setAgentMessage(
                                'Kein Problem! Wir führen das Onboarding manuell durch. Du kannst deine Daten Schritt für Schritt eingeben.',
                            );
                            onNext();
                        }}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        Manuelles Onboarding
                    </Button>
                </Box>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                aria-label="CV-Datei auswählen"
            />
        </Box>
    );
}
